"use client";

import {
  lazy,
  Suspense,
  useEffect,
  useState,
  type ComponentType,
} from "react";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { afterAppShellReady } from "@/components/layout/app-shell-ready";

export const ISLAND_LOAD_WAIT_MS = 5_000;
export const ISLAND_IDLE_TIMEOUT_MS = 2_000;

function afterLoadWhenIdle(callback: () => void): () => void {
  let loadTimer: number | undefined;
  let idleHandle: number | undefined;
  let idleTimer: number | undefined;
  let isScheduled = false;
  const scheduleIdle = () => {
    if (isScheduled) return;
    isScheduled = true;
    window.removeEventListener("load", scheduleIdle);
    window.clearTimeout(loadTimer);
    if (window.requestIdleCallback) {
      idleHandle = window.requestIdleCallback(callback, {
        timeout: ISLAND_IDLE_TIMEOUT_MS,
      });
    } else {
      idleTimer = window.setTimeout(callback, 0);
    }
  };
  if (document.readyState === "complete") {
    scheduleIdle();
  } else {
    window.addEventListener("load", scheduleIdle, { once: true });
    loadTimer = window.setTimeout(scheduleIdle, ISLAND_LOAD_WAIT_MS);
  }
  return () => {
    window.removeEventListener("load", scheduleIdle);
    window.clearTimeout(loadTimer);
    window.clearTimeout(idleTimer);
    if (idleHandle !== undefined) window.cancelIdleCallback(idleHandle);
  };
}

export function deferredIsland<Props extends object>(
  load: () => Promise<ComponentType<Props>>,
  label: string,
): ComponentType<Props> {
  const Island = lazy(() =>
    load().then((component) => ({ default: component })),
  );

  return function DeferredIsland(props: Props) {
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
      let cancelShellWait = () => {};
      const cancelIdleWait = afterLoadWhenIdle(() => {
        cancelShellWait = afterAppShellReady(() => setIsMounted(true));
      });
      return () => {
        cancelIdleWait();
        cancelShellWait();
      };
    }, []);
    if (!isMounted) return null;

    return (
      <ErrorBoundary label={label} fallback={null}>
        <Suspense fallback={null}>
          <Island {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  };
}
